import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {useState} from 'react';
import FilterPanel from './filter-panel/FilterPanel';
import './ViewProjects.scss';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {projectColumns} from '../projectManagementUtils';
import {PAGE_SIZE, RoleNames, RoleId} from '../../../shared/constants';
import {GridRenderCellParams, GridSortModel} from '@mui/x-data-grid';
import {useProjects} from '../../../hooks/Projects/useProjects';
import {useNavigate} from 'react-router-dom';
import {
  resetProjectForm,
  setProjectId,
  setShowToastSave,
  setType,
} from '../../../redux/slice/projectFormSlice';
import {useSelector, useDispatch} from 'react-redux';
import {RootState} from '../../../redux/store';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';

const ROW_HEIGHT = 36;

const ViewProjects: React.FC = () => {
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const roleImpersonationPrefill = useSelector(
    (state: RootState) => state.roleImpersonation.prefill
  );

  const {showToast, showToastType, showToastMessage} = useSelector(
    (state: RootState) => state.projectForm
  );

  const userRole = loginUserData?.roleName;
  const isSystemAdmin = userRole === RoleNames.SystemAdministrator;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isDomainAdminUser =
    loginUserData?.roleName === RoleNames.DomainAdministrator ||
    String(loginUserData?.baseRoleId ?? '') === RoleId.DomainAdministrator;

  const defaultClientName = isDomainAdminUser
    ? roleImpersonationPrefill?.domain?.name || ''
    : '';

  const [pageNumber, setPageNumber] = useState(1);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'projectLabel',
      sort: 'asc',
    },
  ]);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [appliedFilters, setAppliedFilters] = useState({
    name: '',
    clientName: defaultClientName,
    alphaCode: '',
  });

  const handleApplyFilters = (
    name: string,
    clientName: string,
    alphaCode: string
  ) => {
    setPageNumber(1);
    setAppliedFilters({
      name,
      clientName: isSystemAdmin ? clientName : '',
      alphaCode,
    });
  };

  const {data: projectListData, isLoading: isProjectsDataLoading} = useProjects(
    `?Pagesize=${pageSize}&PageNumber=${pageNumber}&SortingOrder=${sortModel[0]?.sort || 'asc'}&SortingColumn=${sortModel[0]?.field || 'projectLabel'}&ProjectLabel=${appliedFilters.name}&ClientEntityLabel=${appliedFilters.clientName}&HCode=${appliedFilters.alphaCode}`
  );

  const projectsWithTempId = projectListData?.data.map((u, index) => ({
    ...u,
    tempId: index + 1,
  }));

  const updatedColumns = [
    ...projectColumns.flatMap((col) => {
      if (col.field === 'clientEntityLabel') {
        return [
          col,
          ...(loginUserData.roleName === RoleNames.DomainAdministrator
            ? [{field: 'projectCode', headerName: 'Project Code', flex: 1}]
            : []),
        ];
      }
      if (col.field === 'hcode') {
        return [
          col,
          ...(loginUserData.roleName === RoleNames.SystemAdministrator
            ? [{field: 'projectDBName', headerName: 'Database Name', flex: 1}]
            : []),
        ];
      }
      if (col.field === 'status') {
        return [
          col,
          ...(loginUserData.roleName === RoleNames.SystemAdministrator
            ? [{field: 'decryptedProjectID', headerName: 'ID', flex: 1}]
            : []),
        ];
      }

      return [col];
    }),
    {
      field: '',
      headerName: '',
      width: 50,
      sortable: false,
      headerClassName: 'typo-text label2_size edit-header',
      filterable: false,
      renderCell: (params: GridRenderCellParams) => {
        const {projectID} = params.row;

        return (
          <Box>
            <ArrIconButton
              icon={
                <EditOutlinedIcon
                  data-testid={`edit-outlined-icon-${params.id}`}
                />
              }
              onClick={() => EditNavigation(projectID)}
              ariaLabel=""
              shape="none"
              className="projects-grid-edit-icon"
              data-testid={`edit-button-${params.id}`}
              testId="view-projects-icon-button"
            />
          </Box>
        );
      },
    },
  ];

  const EditNavigation = (projectID: string) => {
    dispatch(resetProjectForm());
    dispatch(setType('edit'));
    dispatch(setProjectId(projectID));
    navigate('/Project/EditProject');
  };

  const onPageChangeHandler = (value: number) => {
    setPageNumber(value);
  };
  const onPageSizeChangeHandler = (value: number) => {
    setPageSize(value);
    setPageNumber(1);
  };
  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };
  return (
    <Box className="view-project-container">
      <Box className="view-project-header">
        <Box className="view-project-title">
          <Typography
            className="typo-text headline3_size view-project-title-text"
            data-testid={`view-projects-title`}
          >
            {t('projectManagement.manageProjects.title')}
          </Typography>
        </Box>
      </Box>
      <FilterPanel
        initialName=""
        initialClientName=""
        initialAlphaCode=""
        onApplyFilters={handleApplyFilters}
        onKeyDownEvent={(e, name, clientName, alphaCode) => {
          if (e.key === 'Enter') {
            handleApplyFilters(name, clientName, alphaCode);
          }
        }}
      />
      <ArrDataGrid
        rowHeight={ROW_HEIGHT}
        rows={projectsWithTempId || []}
        columns={updatedColumns}
        rowCount={projectListData?.totalRecords}
        currentPage={pageNumber}
        itemsPerPage={pageSize}
        setItemsPerPage={onPageSizeChangeHandler}
        paginationMode="server"
        onPageChange={onPageChangeHandler}
        entityType={t('common.entries')}
        primaryKey="tempId"
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
        className="view-projects-grid"
        loading={isProjectsDataLoading}
        showPerPageOptions={true}
        pageSizeOptions={[10, 25, 50, 100]}
        disableVirtualization={true}
        noRowsOverlayCustomText={t('common.arrDataGrid.noResults', {
          entity: t('projectManagement.manageProjects.projects'),
        })}
      />
      {showToast && (
        <ArrToastNotification
          message={showToastMessage ?? ''}
          onClose={() => dispatch(setShowToastSave(false))}
          status={[showToastType]}
        />
      )}
    </Box>
  );
};
export default ViewProjects;
